import React from 'react';
import { MotionMode, UgcAdScene, TelopPosition, TelopSize } from '../../services/api/ugcAdService';
import { UgcAdSceneDraft } from './types';
import GenerationProgress from './GenerationProgress';
import { TaskyButton, TaskyInput, TaskySelect, TaskyEmptyState } from '../tasky';
import {
  Video,
  RefreshCw,
  Loader2,
  AlertCircle,
  Lock,
  Sparkles,
  Image as ImageIcon,
  EyeOff,
  Wand2,
  ArrowRight,
  ChevronRight,
} from 'lucide-react';

/**
 * ステップ④: モーション（song-ad MotionStep 踏襲）。
 *
 * シーンごとに「動画化する（clip）/ 静止画で使う（still）/ 使わない（skip）」の3値を選び、
 * 動画化するシーンだけストーリーボードを開始フレームとしてクリップを生成する。
 * UGCではトーク中心のシーンは出演者が実際に話す映像（発話音声込み）になるため、
 * トークシーンは原則「動画化」を推奨する（AIにおまかせが優先的に動画化に振る）。
 */

interface MotionStepProps {
  scenes: UgcAdScene[];
  sceneDrafts: UgcAdSceneDraft[];
  generatingSceneIds: Set<string>;
  isSavingDrafts: boolean;
  isMotionAutoRequesting?: boolean;
  onSetMotionMode: (sceneId: string, mode: MotionMode) => void;
  onSetMotionModeAll: (mode: MotionMode) => void;
  onMotionPromptChange: (sceneId: string, prompt: string) => void;
  onTelopPositionChange: (sceneId: string, position: TelopPosition) => void;
  onTelopSizeChange: (sceneId: string, size: TelopSize) => void;
  /** 「保存して次へ」。保存成功後に⑤プレビューへ自動遷移する（呼び出し元が遷移を担当） */
  onSaveAndProceed: () => Promise<void>;
  onMotionAuto: () => Promise<void>;
  onGenerate: (sceneId: string) => void;
  onGenerateAll: (options?: { skipDraftSave?: boolean; suppressToast?: boolean }) => void;
}

const PROGRESS_STEP_LABELS: Record<string, string> = {
  initializing: '初期化中',
  optimizing_prompt: '準備中',
  generating: '動画を生成中',
  downloading: 'ダウンロード中',
  uploading: 'アップロード中',
  saving: '保存中',
};

/**
 * シーンカードの状態バッジ（UGC-06）。
 * 成功／生成中／失敗 をカード上で判別できるようにする。以前は失敗時に
 * サーバーの例外文（cURL の原文）がそのまま出ていた。
 */
const CLIP_STATE_BADGES: Record<string, { label: string; className: string }> = {
  completed: { label: '完成', className: 'bg-green-100 text-green-700' },
  processing: { label: '生成中', className: 'bg-blue-100 text-blue-700' },
  pending: { label: '順番待ち', className: 'bg-blue-100 text-blue-700' },
  failed: { label: '失敗', className: 'bg-red-100 text-red-600' },
  none: { label: '未生成', className: 'bg-gray-100 text-gray-500' },
};

/** クリップ生成の処理段階（全体の待ち時間の見通し。個別の進捗はカード側の progress_step が出す） */
const CLIP_STAGES = [
  '動きの指示を読み込んでいます',
  '映像を生成しています',
  '発話音声を合成しています',
  '仕上げています',
];

const MOTION_MODE_OPTIONS: { key: MotionMode; label: string }[] = [
  { key: 'clip', label: '動画化' },
  { key: 'still', label: '静止画' },
  { key: 'skip', label: '使わない' },
];

const MotionStep: React.FC<MotionStepProps> = ({
  scenes,
  sceneDrafts,
  generatingSceneIds,
  isSavingDrafts,
  isMotionAutoRequesting = false,
  onSetMotionMode,
  onSetMotionModeAll,
  onMotionPromptChange,
  onTelopPositionChange,
  onTelopSizeChange,
  onSaveAndProceed,
  onMotionAuto,
  onGenerate,
  onGenerateAll,
}) => {
  if (scenes.length === 0) {
    return (
      <TaskyEmptyState
        icon={<Video />}
        title="先にストーリーボードを作成してください"
        description="「③ ストーリーボード」で全シーンの画像を生成するとモーションを設定できます"
      />
    );
  }

  const draftFor = (sceneId: string): UgcAdSceneDraft | undefined =>
    sceneDrafts.find((d) => d.id === sceneId);
  const modeFor = (scene: UgcAdScene): MotionMode => draftFor(scene.id)?.motionMode ?? scene.motion_mode;

  const readyScenes = scenes.filter(
    (s) =>
      modeFor(s) === 'clip' &&
      s.storyboard.image &&
      (s.clip.status === 'none' || s.clip.status === 'failed')
  );
  const activeCount = scenes.filter(
    (s) => s.clip.status === 'pending' || s.clip.status === 'processing'
  ).length;
  /**
   * 「動画化」なのにクリップが揃っていないシーン（生成中も含む）。
   * ここが0件になるまでプレビューへ進ませない（UGC-05）。
   */
  const pendingCount = scenes.filter((s) => modeFor(s) === 'clip' && s.storyboard.image && !s.clip.video).length;
  const clipTargets = scenes.filter((s) => modeFor(s) === 'clip').length;
  const doneCount = clipTargets - scenes.filter((s) => modeFor(s) === 'clip' && !s.clip.video).length;

  return (
    <div>
      {/* 何をする画面なのかを最初に言う（UGC-04）。以前は説明が無く、
          クリップ生成が必須工程であること自体が分からなかった */}
      <div className="mb-4 rounded-xl bg-[#F4F3EF] px-4 py-3">
        <h2 className="text-sm font-semibold text-dark">各シーンの画像へ動きを付け、動画クリップを作成します</h2>
        <p className="text-xs text-gray-600 mt-1">
          動きの指示はAIが用意済みです。そのまま「
          {pendingCount > 0 ? `未生成${pendingCount}件を生成して次へ` : '保存して次へ'}
          」を押すだけで進めます。
          トークのシーンは出演者が実際に話す映像（発話音声込み）になります。
          クリップの生成は1本ごとに動画生成分のクレジットを消費します（1本あたり数分）。
        </p>
      </div>

      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-gray-600">
          {clipTargets > 0 ? `動画化するシーン ${doneCount}/${clipTargets} 件が完成しています。` : '動画化するシーンがありません。'}
        </p>
        <div className="flex items-center gap-2">
          {readyScenes.length > 0 && pendingCount !== readyScenes.length && (
            <TaskyButton variant="outline" size="sm" onClick={() => onGenerateAll()}>
              <Sparkles className="w-3.5 h-3.5" />
              未生成を一括生成（{readyScenes.length}件）
            </TaskyButton>
          )}
          {/* 主要導線は1つ。未生成が残っているなら「生成してから進む」しか選べない */}
          <TaskyButton
            size="sm"
            onClick={() => onSaveAndProceed()}
            isLoading={isSavingDrafts || activeCount > 0}
            disabled={isSavingDrafts || activeCount > 0}
          >
            {activeCount > 0 ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {doneCount}/{clipTargets}件 生成中…
              </>
            ) : pendingCount > 0 ? (
              <>
                <Sparkles className="w-3.5 h-3.5" />
                未生成{pendingCount}件を生成して次へ
              </>
            ) : (
              <>
                <ArrowRight className="w-3.5 h-3.5" />
                保存して次へ
              </>
            )}
          </TaskyButton>
        </div>
      </div>

      <details className="mb-4">
        <summary className="flex cursor-pointer list-none items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
          <ChevronRight className="w-3.5 h-3.5" />
          全体の詳細設定
        </summary>
        <div className="flex flex-wrap items-center gap-2 mt-2">
        <button
          type="button"
          onClick={() => onSetMotionModeAll('clip')}
          className="text-xs text-gray-600 bg-warm-surface hover:bg-gray-100 rounded-lg px-3 py-2 transition-colors"
        >
          すべて動画化
        </button>
        <button
          type="button"
          onClick={() => onSetMotionModeAll('still')}
          className="text-xs text-gray-600 bg-warm-surface hover:bg-gray-100 rounded-lg px-3 py-2 transition-colors"
        >
          すべて静止画
        </button>
        <button
          type="button"
          onClick={() => void onMotionAuto()}
          disabled={isMotionAutoRequesting}
          className="text-xs text-white bg-dark hover:opacity-90 rounded-lg px-3 py-2 transition-colors flex items-center gap-1 disabled:opacity-50"
        >
          {isMotionAutoRequesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
          <span>AIにおまかせ</span>
        </button>
        </div>
      </details>

      {/* 何を処理していて、あと何件かを出す（UGC-02） */}
      <GenerationProgress
        active={activeCount > 0}
        stages={CLIP_STAGES}
        estimate="1本あたり数分かかります"
        counts={{ done: doneCount, total: clipTargets }}
        className="mb-4"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {scenes.map((scene, index) => {
          const status = scene.clip.status;
          const isRequesting = generatingSceneIds.has(scene.id);
          const isActive = status === 'pending' || status === 'processing' || isRequesting;
          const isLocked = !scene.storyboard.image;
          const draft = draftFor(scene.id);
          const mode = modeFor(scene);
          const skipped = mode === 'skip';

          return (
            <div
              key={scene.id}
              className={`border border-warm-border rounded-xl overflow-hidden bg-white ${skipped ? 'opacity-60' : ''}`}
            >
              <div className="aspect-[9/16] bg-gray-100 relative flex items-center justify-center">
                {isLocked ? (
                  <div className="flex flex-col items-center text-gray-400 px-4 text-center">
                    <Lock className="w-7 h-7 mb-2" />
                    <span className="text-xs">先に「③ ストーリーボード」を生成してください</span>
                  </div>
                ) : mode === 'clip' && scene.clip.video ? (
                  <video src={scene.clip.video.url} controls preload="metadata" className="w-full h-full object-cover" />
                ) : mode === 'clip' && isActive ? (
                  <div className="flex flex-col items-center text-gray-400 w-full px-6">
                    <Loader2 className="w-7 h-7 animate-spin mb-2" />
                    <span className="text-xs mb-2">
                      {PROGRESS_STEP_LABELS[scene.clip.progress_step ?? ''] ?? '生成中'}...
                    </span>
                    {scene.clip.progress_percentage != null && (
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-primary h-1.5 rounded-full transition-all"
                          style={{ width: `${scene.clip.progress_percentage}%` }}
                        />
                      </div>
                    )}
                  </div>
                ) : mode === 'clip' && status === 'failed' ? (
                  <div className="flex flex-col items-center text-red-500 px-4 text-center gap-2">
                    <AlertCircle className="w-7 h-7" />
                    {/* サーバー側でサニタイズ済みの文言だけを出す（cURLの原文は届かない） */}
                    <span className="text-xs line-clamp-3">{scene.clip.error_message ?? '生成に失敗しました'}</span>
                    <TaskyButton size="sm" variant="outline" onClick={() => onGenerate(scene.id)} disabled={isRequesting}>
                      <RefreshCw className={`w-3 h-3 ${isRequesting ? 'animate-spin' : ''}`} />
                      このシーンを再試行
                    </TaskyButton>
                  </div>
                ) : (
                  <img src={scene.storyboard.image!.url} alt="" className="w-full h-full object-cover" />
                )}
                {skipped && (
                  <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                    <span className="text-xs text-gray-600 bg-white rounded-full px-3 py-1 shadow flex items-center gap-1">
                      <EyeOff className="w-3 h-3" />
                      使用しない
                    </span>
                  </div>
                )}
                {mode === 'still' && !skipped && (
                  <span className="absolute top-2 right-2 bg-white/85 text-gray-600 text-[10px] rounded-full px-2 py-0.5 flex items-center gap-0.5">
                    <ImageIcon className="w-3 h-3" />
                    静止画
                  </span>
                )}
              </div>

              <div className="p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                    シーン {index + 1}
                    {scene.beat ? <span className="font-normal text-gray-400">・{scene.beat}</span> : null}
                    {mode === 'clip' && !isLocked && (
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                          (CLIP_STATE_BADGES[isRequesting ? 'pending' : status] ?? CLIP_STATE_BADGES.none).className
                        }`}
                      >
                        {(CLIP_STATE_BADGES[isRequesting ? 'pending' : status] ?? CLIP_STATE_BADGES.none).label}
                      </span>
                    )}
                  </span>
                  {mode === 'clip' && (
                    <button
                      onClick={() => onGenerate(scene.id)}
                      disabled={isLocked || isActive || !scene.clip_prompt}
                      className="flex items-center gap-1 px-2 py-1 text-xs border border-warm-border rounded-lg hover:bg-gray-100 disabled:opacity-50"
                    >
                      <RefreshCw className={`w-3 h-3 ${isRequesting ? 'animate-spin' : ''}`} />
                      {scene.clip.video || status === 'failed' ? '再生成' : '生成'}
                    </button>
                  )}
                </div>

                {/* 通常利用者はAIの提案を確認するだけで進める。個別の作り込みは詳細に畳む（UGC-04） */}
                <details className="group">
                  <summary className="flex cursor-pointer list-none items-center gap-1 text-[11px] text-gray-500 hover:text-gray-700">
                    <ChevronRight className="w-3 h-3 transition-transform group-open:rotate-90" />
                    このシーンを詳しく設定する
                  </summary>

                  <div className="mt-2 space-y-2">
                    <div className="inline-flex w-full rounded-lg overflow-hidden border border-warm-border text-xs">
                      {MOTION_MODE_OPTIONS.map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => onSetMotionMode(scene.id, opt.key)}
                          className={`flex-1 py-1.5 transition-colors ${
                            mode === opt.key ? 'bg-primary text-white font-medium' : 'text-gray-500 hover:bg-gray-100'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>

                    {mode === 'clip' && (
                      <div>
                        <label className="block text-[11px] text-gray-500 mb-0.5">
                          動きの指示（AIが用意済み。変えたいときだけ編集）
                        </label>
                        <TaskyInput
                          value={draft?.motionPrompt ?? scene.motion_prompt ?? ''}
                          onChange={(e) => onMotionPromptChange(scene.id, e.target.value)}
                          placeholder="例：驚いた表情で商品を見せる"
                          className="text-xs"
                        />
                      </div>
                    )}
                  </div>
                </details>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] text-gray-500 mb-0.5">テロップ位置</label>
                    <TaskySelect
                      value={draft?.telopPosition ?? scene.telop_position}
                      onChange={(e) => onTelopPositionChange(scene.id, e.target.value as TelopPosition)}
                      className="text-xs"
                    >
                      <option value="top">上</option>
                      <option value="middle">中</option>
                      <option value="bottom">下</option>
                    </TaskySelect>
                  </div>
                  <div>
                    <label className="block text-[11px] text-gray-500 mb-0.5">テロップサイズ</label>
                    <TaskySelect
                      value={draft?.telopSize ?? scene.telop_size}
                      onChange={(e) => onTelopSizeChange(scene.id, e.target.value as TelopSize)}
                      className="text-xs"
                    >
                      <option value="sm">小</option>
                      <option value="md">中</option>
                      <option value="lg">大</option>
                    </TaskySelect>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MotionStep;
